import React, { useEffect, useState } from "react"
import classNames from "~/utils/classNames"
import { useDebouncedCallback } from "use-debounce"
import { NewReservationAction } from "~/reducers/newReservation"
import Button from "~/components/ui/Button"

interface TakeDepositFieldProps {
  isAcceptingDeposit: boolean
  reservation: {
    deposit_in_cents: number | undefined
  }
  orderUnpaidSubTotal: number | undefined
  dispatch: React.Dispatch<{
    type: string
    payload: {
      deposit_in_cents: number
    }
  }>
  handleDepositButton: () => void
  className?: string
  buttonClassName?: string
}

const TakeDepositField: React.FC<TakeDepositFieldProps> = ({
  isAcceptingDeposit,
  reservation,
  orderUnpaidSubTotal,
  dispatch,
  handleDepositButton,
  className,
  buttonClassName,
}) => {
  const [localDeposit, setLocalDeposit] = useState<string>(
    reservation.deposit_in_cents !== undefined
      ? (reservation.deposit_in_cents / 100).toString()
      : ""
  )

  const debouncedUpdate = useDebouncedCallback((value: number) => {
    dispatch({
      type: NewReservationAction.UPDATE_DEPOSIT_AMOUNT,
      payload: {
        deposit_in_cents: value,
      },
    })
  }, 300)

  useEffect(() => {
    if (
      reservation.deposit_in_cents !== undefined &&
      parseFloat(localDeposit) !== reservation.deposit_in_cents / 100
    ) {
      setLocalDeposit((reservation.deposit_in_cents / 100).toString())
    }
    if (reservation.deposit_in_cents === undefined && localDeposit !== "") {
      setLocalDeposit("")
    }
  }, [reservation.deposit_in_cents])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value

    if (/^(|[0-9]*\.?[0-9]*)$/.test(rawValue)) {
      if (rawValue === "" || rawValue === ".") {
        setLocalDeposit(rawValue)
        debouncedUpdate(0)
        return
      }

      const parsedValue = parseFloat(rawValue)
      const max = orderUnpaidSubTotal ?? Number.MAX_SAFE_INTEGER

      if (!isNaN(parsedValue) && parsedValue >= 0) {
        const clampedValue = Math.min(parsedValue, max)
        setLocalDeposit(Number(rawValue) > clampedValue ? clampedValue.toFixed(2) : rawValue)
        debouncedUpdate(clampedValue)
      }
    }
  }

  return (
    <>
      {isAcceptingDeposit && (
        <div className={classNames("w-48", className)}>
          <div className="mb-1 flex justify-between items-center">
            <label
              htmlFor="deposit_in_cents"
              className={classNames("inline font-bold text-md text-gray-900", !isAcceptingDeposit && "opacity-0")}
            >
              Deposit amount
            </label>
          </div>

          <div className="relative flex flex-grow items-stretch focus-within:z-10 shadow-sm -mb-1.5">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="text"
              value={localDeposit}
              name="deposit_in_cents"
              id="deposit_in_cents"
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 pl-7 focus:border-green-600 focus:ring-green-600 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
              style={{ paddingLeft: "1.75rem" }}
              placeholder="0.00"
              aria-describedby="price-currency"
              inputMode="decimal"
              min={0}
              max={orderUnpaidSubTotal}
            />
          </div>
        </div>
      )}
      <Button
        variant="none"
        className={classNames(
          "!px-2 !py-1 transition-colors duration-200 cursor-pointer text-green-600 flex items-center",
          buttonClassName,
        )}
        onClick={handleDepositButton}
      >
        {isAcceptingDeposit ? "Remove" : "Take"} deposit
      </Button>
    </>
  )
}

export default TakeDepositField
